/**
 * Origem do pedido (ponto de partida nomeado).
 *
 * Regra vinculativa: o ponto de partida SÓ pode vir do texto ORIGINAL escrito
 * pela pessoa. Respostas de clarificação ("16", "JFK", "sim") são acrescentadas
 * ao pedido para efeito de logística, mas nunca podem transformar-se numa
 * morada de origem — foi isso que produziu origens corrompidas do tipo
 * "Nova Iorque às 16 horas ao aeroporto JFK …".
 *
 * Módulo puro: sem IO, sem React, sem relógio.
 */

import { extractStartPlace } from "./trip-intent";

export type OriginContext = {
  /** Texto tal como a pessoa o escreveu, antes de qualquer merge. */
  originalRequest: string;
  /** Texto já com respostas de clarificação acrescentadas. */
  mergedRequest?: string;
};

export type OriginResolution = {
  /** Ponto de partida aceite (apenas se provado no texto original). */
  startPlace?: string;
  /** Valor recusado por ter nascido de uma resposta de clarificação. */
  rejected?: string;
  source: "original" | "none";
};

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/\s+/gu, " ")
    .trim()
    .toLowerCase();

/** Parte que só existe no texto fundido — nunca autoridade sobre a origem. */
export function clarificationSuffix(original: string, merged?: string): string {
  const base = (original ?? "").replace(/\s+/gu, " ").trim();
  const full = (merged ?? "").replace(/\s+/gu, " ").trim();
  if (!full || full === base) return "";
  return full.startsWith(base) ? full.slice(base.length).trim() : "";
}

/**
 * Resolve a origem. `candidate` é o que o parser (determinístico ou IA) leu do
 * texto fundido; só é aceite quando o mesmo lugar está provado no original.
 */
export function resolveRequestOrigin(ctx: OriginContext, candidate?: string | null): OriginResolution {
  const original = (ctx.originalRequest ?? "").replace(/\s+/gu, " ").trim();
  const proven = original ? extractStartPlace(original) : undefined;
  const wanted = (candidate ?? "").trim();

  if (!wanted) {
    return proven ? { startPlace: proven, source: "original" } : { source: "none" };
  }
  // Aceite quando é exatamente o que o texto original prova, ou quando o
  // próprio nome aparece literalmente no texto original.
  const foldedOriginal = fold(original);
  const foldedWanted = fold(wanted);
  if ((proven && fold(proven) === foldedWanted) || (foldedWanted.length >= 3 && foldedOriginal.includes(foldedWanted))) {
    return { startPlace: wanted, source: "original" };
  }
  return proven
    ? { startPlace: proven, rejected: wanted, source: "original" }
    : { rejected: wanted, source: "none" };
}
